export const availabilityFromStatus = (status = 'on-work') => {
  const mapping = {
    'on-work': 'available',
    'off-work': 'off',
    'in-vacation': 'unavailable',
    holiday: 'unavailable',
    'sick-leave': 'unavailable',
  };
  return mapping[status] ?? 'available';
};

export const formatDate = (value) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const formatTimestamp = (value) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const formatFileSize = (bytes) => {
  if (typeof bytes !== 'number' || Number.isNaN(bytes) || bytes < 0) {
    return '—';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const precision = value < 10 && unitIndex > 0 ? 1 : 0;
  return `${value.toFixed(precision)} ${units[unitIndex]}`;
};

const REMOVAL_GRACE_PERIOD_MS = 30 * 24 * 60 * 60 * 1000;

const toIsoTimestamp = (value) => {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString();
};

const calculateRemovalDeadline = (removedAtIso) => {
  if (!removedAtIso) {
    return null;
  }

  const removedDate = new Date(removedAtIso);
  if (Number.isNaN(removedDate.getTime())) {
    return null;
  }

  const deadline = new Date(removedDate.getTime() + REMOVAL_GRACE_PERIOD_MS);
  if (Number.isNaN(deadline.getTime())) {
    return null;
  }

  return deadline.toISOString();
};

const roundVacationDays = (value) => Math.round((Number(value) || 0) * 100) / 100;

const normalizeVacationDaysRecord = (record) => {
  if (!record || typeof record !== 'object') {
    return {};
  }

  const entries = Array.isArray(record) ? record : Object.entries(record);

  return entries.reduce((acc, entry) => {
    if (!Array.isArray(entry)) {
      return acc;
    }

    const [rawYear, rawValue] = entry;
    const yearKey = String(rawYear ?? '').trim();

    if (!yearKey) {
      return acc;
    }

    let numericValue;
    if (typeof rawValue === 'number') {
      numericValue = rawValue;
    } else if (typeof rawValue === 'string') {
      numericValue = Number(rawValue);
    } else if (
      rawValue &&
      typeof rawValue === 'object' &&
      typeof rawValue.toString === 'function'
    ) {
      numericValue = Number(rawValue.toString());
    } else {
      numericValue = NaN;
    }

    if (!Number.isFinite(numericValue) || numericValue < 0) {
      return acc;
    }

    acc[yearKey] = roundVacationDays(numericValue);
    return acc;
  }, {});
};

const resolveVacationDaysPerYear = (...candidates) => {
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate >= 0) {
      return roundVacationDays(candidate);
    }

    if (typeof candidate === 'string') {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed >= 0) {
        return roundVacationDays(parsed);
      }
    }

    if (
      candidate &&
      typeof candidate === 'object' &&
      typeof candidate.toString === 'function'
    ) {
      const parsed = Number(candidate.toString());
      if (Number.isFinite(parsed) && parsed >= 0) {
        return roundVacationDays(parsed);
      }
    }
  }

  return null;
};

const extractIdentifier = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (typeof value === 'object') {
    const candidate = value?.id ?? value?._id;
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      if (trimmed) {
        return trimmed;
      }
    }

    if (candidate && typeof candidate.toString === 'function') {
      const stringified = candidate.toString();
      if (stringified && stringified !== '[object Object]') {
        return stringified;
      }
    }

    if (typeof value.toString === 'function') {
      const stringified = value.toString();
      if (stringified && stringified !== '[object Object]') {
        return stringified;
      }
    }
  }

  if (typeof value === 'function' && typeof value.toString === 'function') {
    const stringified = value.toString();
    if (stringified && stringified !== '[object Object]') {
      return stringified;
    }
  }

  return null;
};

export const normalizeBusiness = (record) => {
  if (!record) {
    return null;
  }

  const id = extractIdentifier(record?.id ?? record?._id ?? record);
  const rawName = typeof record?.name === 'string' ? record.name.trim() : '';
  const rawLogo =
    typeof record?.logoUrl === 'string'
      ? record.logoUrl.trim()
      : typeof record?.logoURL === 'string'
        ? record.logoURL.trim()
        : '';
  const locationIds = Array.isArray(record?.locationIds)
    ? record.locationIds
        .map((locationId) => {
          if (typeof locationId === 'string') {
            return locationId.trim();
          }
          return extractIdentifier(locationId) ?? '';
        })
        .filter((value) => value && value.length > 0)
    : [];

  return {
    id: id ?? null,
    name: rawName || (id ? `Business ${id.slice(-4)}` : 'Business'),
    logoUrl: rawLogo || null,
    locationIds,
    raw: record,
  };
};

export const normalizeEmployee = (record) => {
  if (!record) {
    return null;
  }

  const status = record.status ?? 'on-work';
  const name = record?.user?.fullName ?? record?.fullName ?? 'Team Member';

  const locationAssignments = Array.isArray(record?.locationAssignments)
    ? record.locationAssignments
        .map((assignment) => ({
          id: typeof assignment?.id === 'string' ? assignment.id : null,
          name: typeof assignment?.name === 'string' ? assignment.name : '',
          address: typeof assignment?.address === 'string' ? assignment.address : '',
          active: assignment?.active !== false,
        }))
        .filter((assignment) => assignment.id || assignment.name)
    : [];

  const normalizedLocationIds = Array.isArray(record?.locationIds)
    ? record.locationIds
        .map((id) => (typeof id === 'string' ? id.trim() : ''))
        .filter((id) => id.length > 0)
    : [];

  const fallbackLocations = Array.isArray(record?.locations)
    ? record.locations
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .filter((value) => value.length > 0)
    : [];

  const rawLocationValue = typeof record?.location === 'string' ? record.location.trim() : '';
  const locationDetailsRecord = record?.locationDetails ?? record?.locationInfo ?? null;

  const rawBusinessRecord = (() => {
    if (record?.businessDetails) {
      return record.businessDetails;
    }

    if (record?.business && typeof record.business === 'object') {
      return record.business;
    }

    if (typeof record?.business === 'string') {
      return {
        id: record.business,
        name: record?.businessName ?? '',
        locationIds: Array.isArray(record?.businessLocationIds)
          ? record.businessLocationIds
          : [],
      };
    }

    if (typeof record?.businessId === 'string') {
      return {
        id: record.businessId,
        name: record?.businessName ?? '',
        locationIds: Array.isArray(record?.businessLocationIds)
          ? record.businessLocationIds
          : [],
      };
    }

    return null;
  })();

  const businessRecord = normalizeBusiness(rawBusinessRecord);
  const businessId = businessRecord?.id
    ?? (typeof record?.businessId === 'string' ? record.businessId.trim() : null)
    ?? (typeof record?.business === 'string' ? record.business.trim() : null);
  const businessName = businessRecord?.name
    ?? (typeof record?.businessName === 'string' ? record.businessName : '');

  const primaryAssignment = locationAssignments[0] ?? null;
  const fallbackLocationName =
    record?.locationName
    ?? locationDetailsRecord?.name
    ?? rawLocationValue
    ?? '';

  const locationId = primaryAssignment?.id
    ?? normalizedLocationIds[0]
    ?? fallbackLocations[0]
    ?? (locationDetailsRecord?.id ?? locationDetailsRecord?._id ?? null);

  const locationName = primaryAssignment?.name
    ?? fallbackLocationName
    ?? '';

  const locationNames = locationAssignments
    .map((assignment) => assignment.name)
    .filter((name) => typeof name === 'string' && name.trim().length > 0);

  const vacationDaysPerYear = resolveVacationDaysPerYear(
    record?.vacationDaysPerYear,
    record?.raw?.vacationDaysPerYear,
    record?.user?.vacationDaysPerYear,
  );

  const remainingVacationDaysPerYear = normalizeVacationDaysRecord(
    record?.remainingVacationDaysPerYear ?? record?.raw?.remainingVacationDaysPerYear ?? null,
  );

  const toIsoDate = (value) => {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date.toISOString().split('T')[0];
  };

  const resolvedAvatarFileId =
    record?.avatarFileId
    ?? record?.raw?.avatarFileId
    ?? (typeof record?.user === 'object' ? record.user?.avatarFileId : null)
    ?? null;

  const resolvedAvatarUrl =
    record?.avatarUrl
    ?? record?.avatar
    ?? record?.raw?.avatarUrl
    ?? (typeof record?.user === 'object' ? record.user?.avatarUrl : null)
    ?? null;

  const removedAtIso = toIsoTimestamp(record?.removedAt ?? record?.raw?.removedAt);
  const removalDeadlineIso = calculateRemovalDeadline(removedAtIso);

  return {
    id: record?.id ?? record?._id ?? record?.employeeId,
    employeeId: record?.employeeId,
    name,
    fullName: record?.fullName ?? record?.user?.fullName ?? name,
    role: record?.role ?? '',
    department: record?.department ?? '',
    location: locationName,
    locationId: locationId ?? '',
    locationDetails: primaryAssignment
      ? {
          id: primaryAssignment?.id,
          name: primaryAssignment?.name,
          address: primaryAssignment?.address,
          active: primaryAssignment?.active,
        }
      : locationDetailsRecord
        ? {
            id: locationDetailsRecord?.id ?? locationDetailsRecord?._id ?? locationId ?? null,
            name: locationDetailsRecord?.name ?? locationName,
            address: locationDetailsRecord?.address ?? '',
            active: locationDetailsRecord?.active ?? true,
          }
        : null,
    locationIds: normalizedLocationIds.length
      ? normalizedLocationIds
      : locationAssignments.map((assignment) => assignment.id).filter(Boolean),
    locationNames,
    locations: locationAssignments,
    birthday: toIsoDate(record?.birthday),
    gender:
      typeof record?.gender === 'string'
        ? record.gender
        : typeof record?.raw?.gender === 'string'
          ? record.raw.gender
          : null,
    phone: record?.phone ?? '',
    email: record?.email ?? record?.user?.email ?? '',
    avatarFileId: resolvedAvatarFileId ?? null,
    avatarUrl: resolvedAvatarUrl ?? null,
    status,
    isManager:
      record?.user?.role === 'manager' ||
      /manager/i.test(record?.role ?? '') ||
      record?.tags?.includes?.('manager'),
    hireDate: formatDate(record?.hireDate) ?? '—',
    shiftPreference: record?.shiftPreference ?? 'Not specified',
    address: record?.address ?? '',
    emergencyContact: record?.emergencyContact ?? {},
    vacationDaysPerYear,
    remainingVacationDaysPerYear,
    availability: availabilityFromStatus(status),
    tags: record?.tags ?? [],
    documents: Array.isArray(record?.documents)
      ? record.documents
          .map(normalizeDocument)
          .filter((document) => Boolean(document))
      : [],
    businessId: businessId ?? null,
    businessName,
    business: businessRecord
      ? {
          id: businessRecord.id,
          name: businessRecord.name,
          locationIds: [...businessRecord.locationIds],
          raw: businessRecord.raw,
        }
      : null,
    removed: Boolean(record?.removed),
    removedAt: removedAtIso,
    removalDeadline: removalDeadlineIso,
    raw: record,
  };
};

export const normalizeDocument = (document) => {
  if (!document) {
    return null;
  }

  const fileName = document?.fileName ?? document?.name ?? 'Document';
  const contentType = document?.contentType ?? document?.type ?? 'file';
  const downloadUrl =
    typeof document?.downloadUrl === 'string' ? document.downloadUrl : null;
  const previewUrl =
    typeof document?.previewUrl === 'string' ? document.previewUrl : downloadUrl;

  return {
    id: document?.id ?? document?._id,
    name: fileName,
    fileName,
    contentType,
    type: contentType,
    size: formatFileSize(document?.size),
    uploadedAt: formatTimestamp(document?.uploadedAt ?? document?.createdAt),
    description: document?.description ?? '',
    tags: document?.tags ?? [],
    downloadUrl,
    previewUrl,
  };
};

export const normalizeTimeOffRequest = (request) => {
  if (!request) {
    return null;
  }

  const employeeRecord = normalizeEmployee(request.employee);

  const start = request?.startDate ? new Date(request.startDate) : null;
  const end = request?.endDate ? new Date(request.endDate) : null;
  const submitted = request?.submittedDate ? new Date(request.submittedDate) : null;

  const candidateDayPart =
    typeof request?.dayPart === 'string' ? request.dayPart : 'full-day';
  const validDayParts = new Set(['full-day', 'forenoon', 'afternoon']);
  const rawDayPart = validDayParts.has(candidateDayPart) ? candidateDayPart : 'full-day';
  const isSameDay =
    start &&
    end &&
    start.toDateString() === end.toDateString();
  const normalizedDayPart = isSameDay ? rawDayPart : 'full-day';

  const duration = request?.duration
    ?? (isSameDay && normalizedDayPart !== 'full-day'
      ? 0.5
      : start && end
        ? Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1)
        : 1);

  const employeeSnapshot = employeeRecord
    ? {
        id: employeeRecord.id,
        name: employeeRecord.name,
        position: employeeRecord.role,
        department: employeeRecord.department,
        email: employeeRecord.email,
        accountEmail:
          request?.employee?.user?.email
          ?? employeeRecord.email,
        avatarFileId: employeeRecord.avatarFileId,
        avatarUrl: employeeRecord.avatarUrl,
        status: employeeRecord.status,
        location: employeeRecord.location,
      }
    : null;

  const ensureDateString = (value) => {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date.toISOString().split('T')[0];
  };

  const buildComment = (comment, index) => {
    const created = comment?.createdAt ? new Date(comment.createdAt) : null;
    const authorObject = comment?.author && typeof comment.author === 'object'
      ? comment.author
      : null;

    const authorId = authorObject?.id
      ?? authorObject?._id
      ?? (typeof comment?.author === 'string' ? comment.author : null);

    const authorName = authorObject?.fullName
      ?? comment?.authorName
      ?? (authorId ? `User ${authorId.slice?.(-4) ?? ''}` : 'Manager');

    return {
      id: comment?.id ?? comment?._id ?? `${request?.id}-comment-${index}`,
      authorId,
      author: authorName,
      date: formatDate(created),
      createdAt: created?.toISOString?.() ?? null,
      text: comment?.comment ?? comment?.text ?? '',
    };
  };

  return {
    id: request?.id ?? request?._id,
    employeeId: employeeRecord?.id ?? request?.employee?.id ?? request?.employee?._id,
    employee: employeeSnapshot,
    type: request?.type ?? 'vacation',
    startDate: ensureDateString(start),
    endDate: ensureDateString(end),
    duration,
    reason: request?.reason ?? '',
    status: request?.status ?? 'pending',
    submittedDate: formatDate(submitted),
    submittedTimestamp: submitted?.toISOString?.() ?? null,
    coverageImpact: request?.coverageImpact ?? null,
    managerResponse: request?.managerResponse ?? null,
    dayPart: normalizedDayPart ?? 'full-day',
    comments: (request?.comments ?? []).map(buildComment),
    raw: request,
  };
};

export const normalizeConversation = (conversation, usersMap = {}, currentUserId = null) => {
  if (!conversation) {
    return null;
  }

  const participants = conversation?.participants ?? [];
  const normalizedCurrentUserIds = Array.isArray(currentUserId)
    ? currentUserId
    : [currentUserId];
  const currentUserIdSet = new Set(
    normalizedCurrentUserIds
      .map((value) => (value != null ? String(value).trim() : ''))
      .filter(Boolean),
  );
  const normalizedType = (() => {
    const candidate = conversation?.type;
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim().toLowerCase();
    }
    return 'direct';
  })();

  const normalizedUnseenCount = (() => {
    const rawCount = conversation?.unseenCount ?? conversation?.unreadCount ?? 0;
    const numeric = Number(rawCount);
    if (Number.isFinite(numeric) && numeric >= 0) {
      return Math.floor(numeric);
    }
    return 0;
  })();

  const resolveParticipantObject = (participant) => (participant && typeof participant === 'object'
    ? participant
    : {});

  const resolveParticipantId = (participant) => {
    if (participant && typeof participant === 'object') {
      return participant?.id ?? participant?._id ?? participant?.userId ?? null;
    }
    return participant ?? null;
  };

  const participantNames = participants
    .map((participant) => {
      const participantObject = resolveParticipantObject(participant);
      const userId = resolveParticipantId(participant);
      const mappedEntry = userId ? usersMap[userId] ?? {} : {};
      return mappedEntry?.name
        ?? participantObject?.fullName
        ?? participantObject?.name
        ?? participantObject?.email
        ?? null;
    })
    .filter(Boolean);

  const isCurrentUserParticipant = (participant) => {
    if (!currentUserIdSet.size) {
      return false;
    }
    const participantId = resolveParticipantId(participant);
    if (!participantId) {
      return false;
    }
    return currentUserIdSet.has(String(participantId));
  };

  const otherParticipantNames = participants
    .filter((participant) => !isCurrentUserParticipant(participant))
    .map((participant) => {
      const participantObject = resolveParticipantObject(participant);
      const userId = resolveParticipantId(participant);
      const mappedEntry = userId ? usersMap[userId] ?? {} : {};
      return mappedEntry?.name
        ?? participantObject?.fullName
        ?? participantObject?.name
        ?? participantObject?.email
        ?? null;
    })
    .filter(Boolean);

  const fallbackNameParts =
    otherParticipantNames.length > 0 ? otherParticipantNames : participantNames;

  const currentUserNameCandidates = (() => {
    if (!currentUserIdSet.size) {
      return [];
    }

    const mappedCurrentUser = (() => {
      for (const id of currentUserIdSet) {
        if (usersMap[id]) {
          return usersMap[id];
        }
      }
      return {};
    })();

    const participantEntry = participants.find((participant) =>
      isCurrentUserParticipant(participant)
    );
    const participantObject = resolveParticipantObject(participantEntry);

    const rawCandidates = [
      mappedCurrentUser?.name,
      mappedCurrentUser?.email,
      mappedCurrentUser?.displayName,
      mappedCurrentUser?.fullName,
      participantObject?.fullName,
      participantObject?.name,
      participantObject?.email,
    ];

    return rawCandidates
      .map((value) => (typeof value === 'string' ? value.trim() : ''))
      .filter(Boolean);
  })();

  const currentUserNameSet = new Set(
    currentUserNameCandidates.map((name) => name.toLowerCase())
  );

  const sanitizeLabel = (rawLabel) => {
    if (typeof rawLabel !== 'string') {
      return null;
    }

    const trimmed = rawLabel.trim();
    if (!trimmed) {
      return null;
    }

    if (!currentUserNameSet.size) {
      return trimmed;
    }

    const lowerCaseLabel = trimmed.toLowerCase();
    const includesCurrentUser = Array.from(currentUserNameSet).some((candidate) =>
      lowerCaseLabel.includes(candidate)
    );

    if (!includesCurrentUser) {
      return trimmed;
    }

    const parts = trimmed
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length > 1) {
      const filteredParts = parts.filter(
        (part) => !currentUserNameSet.has(part.toLowerCase())
      );
      if (filteredParts.length > 0) {
        return filteredParts.join(', ');
      }
    }

    if (fallbackNameParts.length > 0) {
      return fallbackNameParts.join(', ');
    }

    return parts.length > 0 ? parts.join(', ') : trimmed;
  };

  let conversationAvatarFileId = null;
  let conversationAvatarUrl = null;

  if (normalizedType === 'direct') {
    for (const participant of participants) {
      if (isCurrentUserParticipant(participant)) {
        continue;
      }
      const participantObject = resolveParticipantObject(participant);
      const userId = resolveParticipantId(participant);
      if (!userId) {
        continue;
      }
      const participantEntry = usersMap[userId] ?? {};
      const candidateId = participantEntry?.avatarFileId
        ?? participantObject?.avatarFileId
        ?? null;
      const candidateUrl =
        typeof participantEntry?.avatarUrl === 'string'
          ? participantEntry.avatarUrl
          : typeof participantEntry?.avatar === 'string'
            ? participantEntry.avatar
            : participantObject?.avatarUrl
              ?? participantObject?.avatar
              ?? null;
      if (typeof candidateId === 'string' && candidateId.trim().length > 0) {
        conversationAvatarFileId = candidateId;
        if (candidateUrl) {
          conversationAvatarUrl = candidateUrl;
          break;
        }
      }
      if (!conversationAvatarUrl && candidateUrl) {
        conversationAvatarUrl = candidateUrl;
      }
    }
  }

  const sanitizedConversationName = sanitizeLabel(conversation?.name);
  const fallbackName = fallbackNameParts.length > 0
    ? fallbackNameParts.join(', ')
    : null;

  const defaultName = sanitizedConversationName
    ?? fallbackName
    ?? 'Conversation';
  const directName = (() => {
    if (normalizedType !== 'direct') {
      return defaultName;
    }

    if (fallbackName) {
      return fallbackName;
    }

    return defaultName;
  })();

  const resolvePhoneString = (value) => {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  };

  const initialPhoneCandidates = [
    conversation?.phoneNumber,
    conversation?.phone,
    conversation?.contactPhone,
    conversation?.contact?.phone,
    conversation?.metadata?.phoneNumber,
    conversation?.metadata?.phone,
    conversation?.metadata?.contactPhone,
    conversation?.metadata?.contact?.phone,
  ];

  let resolvedPhoneNumber = null;
  for (const candidate of initialPhoneCandidates) {
    const phone = resolvePhoneString(candidate);
    if (phone) {
      resolvedPhoneNumber = phone;
      break;
    }
  }

  if (!resolvedPhoneNumber && normalizedType === 'direct') {
    for (const participant of participants) {
      if (isCurrentUserParticipant(participant)) {
        continue;
      }
      const participantObject = resolveParticipantObject(participant);
      const participantId = resolveParticipantId(participant);
      const mappedEntry = participantId ? usersMap[participantId] ?? {} : {};
      const candidate = mappedEntry?.phone
        ?? participantObject?.phone
        ?? participantObject?.phoneNumber
        ?? participantObject?.contactPhone
        ?? null;
      const phone = resolvePhoneString(candidate);
      if (phone) {
        resolvedPhoneNumber = phone;
        break;
      }
    }
  }

  return {
    id: conversation?.id ?? conversation?._id,
    type: normalizedType ?? 'direct',
    name: directName,
    avatarFileId: conversationAvatarFileId,
    avatarUrl: conversationAvatarUrl,
    lastMessage: null,
    lastSender: null,
    lastMessageType: 'text',
    timestamp: conversation?.lastMessageAt ? new Date(conversation.lastMessageAt) : null,
    unreadCount: normalizedUnseenCount,
    unseenCount: normalizedUnseenCount,
    isOnline: false,
    memberCount: participants.length,
    priority: conversation?.priority ?? 'normal',
    participants,
    metadata: conversation?.metadata ?? {},
    phoneNumber: resolvedPhoneNumber,
    isDraft: Boolean(conversation?.isDraft),
    draftPayload: conversation?.draftPayload ?? null,
    raw: conversation,
  };
};

export const normalizeMessage = (message, usersMap = {}, currentUserId = null) => {
  if (!message) {
    return null;
  }

  const senderValue = message?.sender;
  const senderId = typeof senderValue === 'object'
    ? senderValue?.id ?? senderValue?._id
    : senderValue;

  const senderObject = (message?.sender && typeof message.sender === 'object') ? message.sender : null;
  const senderInfo = senderId ? usersMap[senderId] ?? null : null;
  const fallbackName = senderObject?.fullName
    ?? senderObject?.name
    ?? senderObject?.email
    ?? (typeof senderObject?.toString === 'function' ? senderObject.toString() : null);
  const fallbackAvatar = senderObject?.avatarUrl ?? senderObject?.avatar ?? null;
  const fallbackAvatarFileId = senderObject?.avatarFileId ?? null;

  const normalizedCurrentUserIds = Array.isArray(currentUserId)
    ? currentUserId
    : currentUserId != null
      ? [currentUserId]
      : [];
  const currentUserIdSet = new Set(
    normalizedCurrentUserIds
      .map((value) => (value != null ? String(value).trim() : ''))
      .filter(Boolean),
  );

  const parseSeenAt = (value) => {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const seenEntriesRaw = Array.isArray(message?.seenBy) ? message.seenBy : [];
  const normalizedSeenEntries = seenEntriesRaw
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }

      const rawUserId = entry.userId
        ?? entry.id
        ?? entry._id
        ?? entry.recipient
        ?? null;

      if (!rawUserId) {
        return null;
      }

      const userId = String(rawUserId);
      const seenAt = parseSeenAt(entry.seenAt ?? null);

      return { userId, seenAt };
    })
    .filter(Boolean);

  const seenAtFromPayload = parseSeenAt(message?.seenAt ?? null);
  const seenRecordForCurrent = currentUserIdSet.size
    ? normalizedSeenEntries.find((entry) => currentUserIdSet.has(entry.userId)) ?? null
    : null;
  const resolvedSeenAt = seenRecordForCurrent?.seenAt ?? seenAtFromPayload ?? null;

  const isOwnMessage = currentUserIdSet.size && senderId
    ? currentUserIdSet.has(String(senderId))
    : false;

  const hasOtherSeen = normalizedSeenEntries.some(
    (entry) => !currentUserIdSet.has(entry.userId) && entry.seenAt,
  );

  const baseStatus = (() => {
    if (typeof message?.status === 'string' && message.status.trim().length > 0) {
      return message.status.trim();
    }
    return 'sent';
  })();

  const derivedStatus = isOwnMessage && hasOtherSeen ? 'read' : baseStatus;

  return {
    id: message?.id ?? message?._id,
    senderId,
    senderName: senderInfo?.name ?? fallbackName ?? 'Unknown User',
    senderAvatarFileId: senderInfo?.avatarFileId ?? fallbackAvatarFileId ?? null,
    senderAvatarUrl:
      typeof senderInfo?.avatarUrl === 'string'
        ? senderInfo.avatarUrl
        : typeof senderInfo?.avatar === 'string'
          ? senderInfo.avatar
          : fallbackAvatar,
    type: message?.type ?? 'text',
    content: message?.content ?? '',
    timestamp: message?.createdAt ? new Date(message.createdAt) : new Date(),
    status: derivedStatus,
    seenAt: resolvedSeenAt,
    isSeen: Boolean(resolvedSeenAt),
    seenBy: normalizedSeenEntries,
    attachments: message?.attachments ?? null,
    raw: message,
  };
};
